import React from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Linking,
  Platform,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import { useColors, ThemeColors } from "../hooks/useColors";
import { spacing, typography } from "../theme";
import { platformAlert, showAlert } from "../utils/platformAlert";

export default function SupportScreen() {
  const colors = useColors();
  const styles = createStyles(colors);

  const handleEmailSupport = () => {
    const email = "support@jadetradeapp.com";
    const subject = "Support Request - JadeTrade App";
    const body =
      "Hello JadeTrade Support Team,\n\nI need assistance with:\n\n[Please describe your issue here]\n\n";

    platformAlert(
      "Contact Support",
      `Email us at:\n\n${email}`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Open Email",
          onPress: () => {
            const mailtoUrl = `mailto:${email}?subject=${encodeURIComponent(
              subject
            )}&body=${encodeURIComponent(body)}`;
            
            if (Platform.OS === 'web') {
              // On web, use window.location.href for mailto links
              window.location.href = mailtoUrl;
            } else {
              // On native, use Linking
              Linking.openURL(mailtoUrl).catch(() => {
                // Silently fail if email client unavailable - user already has the info from the alert
              });
            }
          },
        },
      ]
    );
  };

  const handleCopyEmail = () => {
    showAlert("Support Email", "support@jadetradeapp.com");
  };

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <View style={styles.iconContainer}>
          <Feather name="help-circle" size={48} color={colors.primary} />
        </View>
        <Text style={styles.title}>How can we help?</Text>
        <Text style={styles.subtitle}>
          Our support team is here to assist you with any questions or issues
          you may have.
        </Text>
      </View>

      <View style={styles.content}>
        {/* Primary Support Contact */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Contact Support</Text>

          <TouchableOpacity
            style={styles.contactCard}
            onPress={handleEmailSupport}
          >
            <View style={styles.contactInfo}>
              <View style={styles.contactIcon}>
                <Feather name="mail" size={24} color={colors.primary} />
              </View>
              <View style={styles.contactText}>
                <Text style={styles.contactTitle}>Email Support</Text>
                <Text style={styles.contactEmail}>
                  support@jadetradeapp.com
                </Text>
                <Text style={styles.contactDescription}>
                  Get help with orders, account issues, or general questions
                </Text>
              </View>
            </View>
            <Feather name="external-link" size={20} color={colors.textLight} />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.copyEmailCard}
            onPress={handleCopyEmail}
          >
            <Feather name="copy" size={20} color={colors.textLight} />
            <Text style={styles.copyEmailText}>Tap to view email address</Text>
          </TouchableOpacity>
        </View>

        {/* Common Topics */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Common Support Topics</Text>

          <View style={styles.topicsList}>
            <View style={styles.topicItem}>
              <Feather name="shopping-cart" size={16} color={colors.primary} />
              <Text style={styles.topicText}>Order issues and tracking</Text>
            </View>

            <View style={styles.topicItem}>
              <Feather name="credit-card" size={16} color={colors.primary} />
              <Text style={styles.topicText}>
                Payment and billing questions
              </Text>
            </View>

            <View style={styles.topicItem}>
              <Feather name="user" size={16} color={colors.primary} />
              <Text style={styles.topicText}>Account management</Text>
            </View>

            <View style={styles.topicItem}>
              <Feather name="truck" size={16} color={colors.primary} />
              <Text style={styles.topicText}>Delivery and shipping</Text>
            </View>

            <View style={styles.topicItem}>
              <Feather name="store" size={16} color={colors.primary} />
              <Text style={styles.topicText}>Seller account setup</Text>
            </View>

            <View style={styles.topicItem}>
              <Feather name="shield" size={16} color={colors.primary} />
              <Text style={styles.topicText}>Safety and security</Text>
            </View>
          </View>
        </View>

        {/* Tips for Better Support */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Tips for Faster Support</Text>
          <View style={styles.tipsCard}>
            <Text style={styles.tipsTitle}>
              When contacting support, please include:
            </Text>

            <View style={styles.tipsList}>
              <View style={styles.tipItem}>
                <Text style={styles.tipBullet}>•</Text>
                <Text style={styles.tipText}>Your account email address</Text>
              </View>

              <View style={styles.tipItem}>
                <Text style={styles.tipBullet}>•</Text>
                <Text style={styles.tipText}>Order number (if applicable)</Text>
              </View>

              <View style={styles.tipItem}>
                <Text style={styles.tipBullet}>•</Text>
                <Text style={styles.tipText}>
                  Detailed description of the issue
                </Text>
              </View>

              <View style={styles.tipItem}>
                <Text style={styles.tipBullet}>•</Text>
                <Text style={styles.tipText}>Screenshots (if helpful)</Text>
              </View>

              <View style={styles.tipItem}>
                <Text style={styles.tipBullet}>•</Text>
                <Text style={styles.tipText}>
                  Device and app version information
                </Text>
              </View>
            </View>
          </View>
        </View>
      </View>
    </ScrollView>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      padding: spacing.xl,
      alignItems: "center",
      backgroundColor: colors.backgroundSecondary,
    },
    iconContainer: {
      marginBottom: spacing.md,
    },
    title: {
      ...typography.title,
      fontSize: 28,
      fontWeight: "700",
      color: colors.text,
      textAlign: "center",
      marginBottom: spacing.sm,
    },
    subtitle: {
      ...typography.body,
      fontSize: 16,
      color: colors.textLight,
      textAlign: "center",
      lineHeight: 22,
    },
    content: {
      padding: spacing.sm,
    },
    section: {
      marginBottom: spacing.xl,
    },
    sectionTitle: {
      ...typography.subtitle,
      fontSize: 20,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.md,
    },
    contactCard: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: spacing.lg,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      borderWidth: 1,
      borderColor: colors.border,
      marginBottom: spacing.sm,
    },
    contactInfo: {
      flexDirection: "row",
      alignItems: "center",
      flex: 1,
    },
    contactIcon: {
      width: 48,
      height: 48,
      borderRadius: 24,
      backgroundColor: colors.backgroundSecondary,
      alignItems: "center",
      justifyContent: "center",
      marginRight: spacing.md,
    },
    contactText: {
      flex: 1,
    },
    contactTitle: {
      ...typography.subtitle,
      fontSize: 16,
      fontWeight: "600",
      color: colors.text,
      marginBottom: 2,
    },
    contactEmail: {
      ...typography.body,
      fontSize: 14,
      color: colors.primary,
      fontWeight: "500",
      marginBottom: 4,
    },
    contactDescription: {
      ...typography.caption,
      fontSize: 12,
      color: colors.textLight,
      lineHeight: 16,
    },
    copyEmailCard: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.backgroundSecondary,
      borderRadius: 8,
      padding: spacing.md,
      gap: spacing.xs,
    },
    copyEmailText: {
      ...typography.body,
      fontSize: 14,
      color: colors.textLight,
    },
    topicsList: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: spacing.lg,
      borderWidth: 1,
      borderColor: colors.border,
    },
    topicItem: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: spacing.sm,
      gap: spacing.md,
    },
    topicText: {
      ...typography.body,
      fontSize: 14,
      color: colors.text,
      flex: 1,
    },
    tipsCard: {
      backgroundColor: colors.backgroundSecondary,
      borderRadius: 12,
      padding: spacing.lg,
      borderLeftWidth: 3,
      borderLeftColor: colors.primary,
    },
    tipsTitle: {
      ...typography.subtitle,
      fontSize: 16,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.md,
    },
    tipsList: {
      gap: spacing.sm,
    },
    tipItem: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: spacing.sm,
    },
    tipBullet: {
      ...typography.body,
      fontSize: 16,
      color: colors.primary,
      fontWeight: "600",
      marginTop: 1,
    },
    tipText: {
      ...typography.body,
      fontSize: 14,
      color: colors.text,
      flex: 1,
      lineHeight: 20,
    },
  });
